import { useMarketingViewSection } from '../components/MarketingViewsProvider';
import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import * as echarts from 'echarts';
import {
  siteDataApi,
  type BounceSummary,
  type BounceTrendRow,
  type BounceChannelRow,
  type BounceDetailRow,
  type BounceParams,
} from '../api/siteData';
import { getCurrentCalendarRange } from '../components/MarketingTimeRangeFilter';
import { ReportExportButton } from '../components/ReportExportButton';
import { useSiteShopName } from '../hooks/useSiteShopName';
import { ReportLabelSelect } from '../components/ReportLabelSelect';
import { TableLoadingCell, TableLoadingOverlay, TableLoadingState } from '../components/TableLoadingOverlay';
import { SortableHeader, useTableSort, type SortValue } from '../components/SortableTable';

// ── 格式化 ──────────────────────────────────────────────────────────
const fmtInt = (v: number) => (v == null ? '-' : Math.round(v).toLocaleString('en-US'));
const fmtPct = (v: number) => (v == null ? '-' : (v * 100).toFixed(2) + '%');

// 默认区间：近 30 天 (昨天往前)。本地时区格式化，避免 toISOString 的 UTC 偏移。
function fmtLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function defaultRange(): { start: string; end: string } {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return { start: fmtLocalDate(start), end: fmtLocalDate(end) };
}

type DatePreset = '7d' | '14d' | '30d' | 'thisWeek' | 'thisMonth' | 'custom';

const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: '7d', label: '近7天' },
  { key: '14d', label: '近14天' },
  { key: '30d', label: '近30天' },
  { key: 'thisWeek', label: '本周' },
  { key: 'thisMonth', label: '本月' },
  { key: 'custom', label: '自定义' },
];

function presetRange(preset: Exclude<DatePreset, 'custom'>): { start: string; end: string } {
  if (preset === 'thisWeek' || preset === 'thisMonth') return getCurrentCalendarRange(preset, fmtLocalDate(new Date()));
  const end = new Date();
  const start = new Date(end);
  const days = preset === '14d' ? 13 : preset === '30d' ? 29 : 6;
  start.setDate(start.getDate() - days);
  return { start: fmtLocalDate(start), end: fmtLocalDate(end) };
}

const USER_TYPES = ['全部', '已支付', '未支付', '未注册'];

// ── echarts 折线图组件 ──────────────────────────────────────────────
interface LineSeries {
  name: string;
  data: [string, number][]; // [date, value(0-1)]
}
function BounceLineChart({ series, height = 360 }: { series: LineSeries[]; height?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    if (!chartRef.current) chartRef.current = echarts.init(ref.current);
    const chart = chartRef.current;

    // 用所有 series 的日期并集作为 x 轴
    const dateSet = new Set<string>();
    series.forEach(s => s.data.forEach(([d]) => dateSet.add(d)));
    const dates = Array.from(dateSet).sort();

    chart.setOption({
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: number) => (v == null ? '-' : (v * 100).toFixed(2) + '%'),
      },
      legend: { type: 'scroll', bottom: 0 },
      grid: { left: 48, right: 24, top: 42, bottom: 56 },
      xAxis: { type: 'category', data: dates, axisLabel: { rotate: 45, fontSize: 10 } },
      yAxis: { type: 'value', axisLabel: { formatter: (v: number) => (v * 100).toFixed(0) + '%' } },
      series: series.map(s => {
        const map = new Map(s.data);
        return {
          name: s.name,
          type: 'line',
          smooth: false,
          showSymbol: true,
          symbolSize: 5,
          label: {
            show: true,
            position: 'top',
            distance: 6,
            fontSize: 10,
            formatter: (params: { value: number | null }) => `${(Number(params.value ?? 0) * 100).toFixed(2)}%`,
          },
          data: dates.map(d => (map.has(d) ? map.get(d)! : null)),
        };
      }),
    }, true);
    return undefined;
  }, [series]);

  // 容器尺寸变化时重绘
  useEffect(() => {
    const onResize = () => chartRef.current?.resize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => () => { chartRef.current?.dispose(); chartRef.current = null; }, []);

  return <div ref={ref} style={{ width: '100%', height }} />;
}

// ── 卡片 ────────────────────────────────────────────────────────────
function SummaryCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card" style={{ flex: 1, minWidth: 180, padding: '16px 20px' }}>
      <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── 渠道跳出率表 ────────────────────────────────────────────────────
const bounceSummaryExportColumns = [{ key: 'metric', label: '指标' }, { key: 'value', label: '值' }];
const bounceTrendExportColumns = [
  { key: 'date', label: '日期' },
  { key: 'user_type', label: '用户类型' },
  { key: 'all_site_uv', label: '全站UV' },
  { key: 'jump_uv', label: '跳出用户数' },
  { key: 'bounce_rate', label: '跳出率' },
];
const bounceChannelExportColumns = [
  { key: 'one_level_channel', label: '一级渠道' },
  { key: 'two_level_channel', label: '二级渠道' },
  { key: 'overall_uv', label: '整体全站UV' },
  { key: 'overall_jump', label: '整体跳出用户数' },
  { key: 'overall_rate', label: '整体跳出率' },
  { key: 'paid_uv', label: '已支付全站UV' },
  { key: 'paid_jump', label: '已支付跳出用户数' },
  { key: 'paid_rate', label: '已支付跳出率' },
  { key: 'unpaid_uv', label: '未支付全站UV' },
  { key: 'unpaid_jump', label: '未支付跳出用户数' },
  { key: 'unpaid_rate', label: '未支付跳出率' },
  { key: 'unreg_uv', label: '未注册全站UV' },
  { key: 'unreg_jump', label: '未注册跳出用户数' },
  { key: 'unreg_rate', label: '未注册跳出率' },
];

function bounceDetailExportColumns(group: DetailGroup) {
  const cols = [{ key: 'date', label: '日期' }];
  if (group === 'user_type_device') cols.push({ key: 'pub_client', label: '终端' });
  if (group === 'user_type_channel') cols.push({ key: 'channel', label: '一级渠道' });
  return [
    ...cols,
    { key: 'user_type', label: '日度用户类型' },
    { key: 'all_site_uv', label: '全站UV' },
    { key: 'jump_uv', label: '跳出用户数' },
    { key: 'no_jump_uv', label: '未跳出用户数' },
    { key: 'bounce_rate', label: '跳出率' },
  ];
}

function ChannelBounceTable({ level, rows, loading, extra }: { level: 1 | 2; rows: BounceChannelRow[]; loading: boolean; extra?: ReactNode }) {
  const dimCount = level;
  const colCount = dimCount + 12;
  const { sort, toggleSort, sortedRows } = useTableSort<BounceChannelRow, string>(
    rows,
    (row, key) => row[key as keyof BounceChannelRow] as SortValue,
  );
  const metricHeaders: Array<{ key: keyof BounceChannelRow; label: string }> = [
    { key: 'overall_uv', label: '全站UV' }, { key: 'overall_jump', label: '跳出用户数' }, { key: 'overall_rate', label: '跳出率' },
    { key: 'paid_uv', label: '全站UV' }, { key: 'paid_jump', label: '跳出用户数' }, { key: 'paid_rate', label: '跳出率' },
    { key: 'unpaid_uv', label: '全站UV' }, { key: 'unpaid_jump', label: '跳出用户数' }, { key: 'unpaid_rate', label: '跳出率' },
    { key: 'unreg_uv', label: '全站UV' }, { key: 'unreg_jump', label: '跳出用户数' }, { key: 'unreg_rate', label: '跳出率' },
  ];
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>{level === 1 ? '一级渠道跳出率' : '二级渠道跳出率'}</span>
        {extra}
      </div>
      <div className="table-wrap" style={{ position: 'relative', maxHeight: 460, overflow: 'auto' }}>
        <table style={{ minWidth: 980 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr>
              <SortableHeader rowSpan={2} sortKey="one_level_channel" sort={sort} onSort={toggleSort} style={{ background: 'var(--surface2)', verticalAlign: 'bottom' }}>一级渠道</SortableHeader>
              {level >= 2 && <SortableHeader rowSpan={2} sortKey="two_level_channel" sort={sort} onSort={toggleSort} style={{ background: 'var(--surface2)', verticalAlign: 'bottom' }}>二级渠道</SortableHeader>}
              <th colSpan={3} style={{ textAlign: 'center', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>整体</th>
              <th colSpan={3} style={{ textAlign: 'center', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>已支付</th>
              <th colSpan={3} style={{ textAlign: 'center', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>未支付</th>
              <th colSpan={3} style={{ textAlign: 'center', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>未注册</th>
            </tr>
            <tr>
              {metricHeaders.map(header => (
                <SortableHeader key={String(header.key)} sortKey={String(header.key)} sort={sort} onSort={toggleSort} align="right" style={{ background: 'var(--surface2)', whiteSpace: 'nowrap' }}>{header.label}</SortableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr><td colSpan={colCount}><TableLoadingCell /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={colCount} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>暂无数据</td></tr>
            ) : sortedRows.map((r, i) => (
              <tr key={i}>
                <td style={{ whiteSpace: 'nowrap' }}>{r.one_level_channel || '-'}</td>
                {level >= 2 && <td style={{ whiteSpace: 'nowrap' }}>{r.two_level_channel || '-'}</td>}
                <td style={tdNum}>{fmtInt(r.overall_uv)}</td><td style={tdNum}>{fmtInt(r.overall_jump)}</td><td style={tdNum}>{fmtPct(r.overall_rate)}</td>
                <td style={tdNum}>{fmtInt(r.paid_uv)}</td><td style={tdNum}>{fmtInt(r.paid_jump)}</td><td style={tdNum}>{fmtPct(r.paid_rate)}</td>
                <td style={tdNum}>{fmtInt(r.unpaid_uv)}</td><td style={tdNum}>{fmtInt(r.unpaid_jump)}</td><td style={tdNum}>{fmtPct(r.unpaid_rate)}</td>
                <td style={tdNum}>{fmtInt(r.unreg_uv)}</td><td style={tdNum}>{fmtInt(r.unreg_jump)}</td><td style={tdNum}>{fmtPct(r.unreg_rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && rows.length > 0 && <TableLoadingOverlay />}
      </div>
    </div>
  );
}

const tdNum: React.CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

// ── 明细表 ──────────────────────────────────────────────────────────
type DetailGroup = 'user_type' | 'user_type_device' | 'user_type_channel';
const DETAIL_TABS: { key: DetailGroup; label: string }[] = [
  { key: 'user_type', label: '分用户类型' },
  { key: 'user_type_device', label: '分用户类型+终端' },
  { key: 'user_type_channel', label: '分用户类型+渠道' },
];

function DetailTable({ group, rows, loading }: { group: DetailGroup; rows: BounceDetailRow[]; loading: boolean }) {
  const showDevice = group === 'user_type_device';
  const showChannel = group === 'user_type_channel';
  const colCount = 5 + (showDevice ? 1 : 0) + (showChannel ? 1 : 0);
  const { sort, toggleSort, sortedRows } = useTableSort<BounceDetailRow, string>(
    rows,
    (row, key) => row[key as keyof BounceDetailRow] as SortValue,
  );
  return (
    <div className="table-wrap" style={{ position: 'relative', maxHeight: 460, overflow: 'auto' }}>
      <table style={{ minWidth: 720 }}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
          <tr>
            <SortableHeader sortKey="date" sort={sort} onSort={toggleSort} style={{ background: 'var(--surface2)' }}>日期</SortableHeader>
            {showDevice && <SortableHeader sortKey="pub_client" sort={sort} onSort={toggleSort} style={{ background: 'var(--surface2)' }}>终端</SortableHeader>}
            {showChannel && <SortableHeader sortKey="channel" sort={sort} onSort={toggleSort} style={{ background: 'var(--surface2)' }}>一级渠道</SortableHeader>}
            <SortableHeader sortKey="user_type" sort={sort} onSort={toggleSort} style={{ background: 'var(--surface2)' }}>日度用户类型</SortableHeader>
            <SortableHeader sortKey="all_site_uv" sort={sort} onSort={toggleSort} align="right" style={{ ...tdNum, background: 'var(--surface2)' }}>全站UV</SortableHeader>
            <SortableHeader sortKey="jump_uv" sort={sort} onSort={toggleSort} align="right" style={{ ...tdNum, background: 'var(--surface2)' }}>跳出用户数</SortableHeader>
            <SortableHeader sortKey="no_jump_uv" sort={sort} onSort={toggleSort} align="right" style={{ ...tdNum, background: 'var(--surface2)' }}>未跳出用户数</SortableHeader>
            <SortableHeader sortKey="bounce_rate" sort={sort} onSort={toggleSort} align="right" style={{ ...tdNum, background: 'var(--surface2)' }}>跳出率</SortableHeader>
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 ? (
            <tr><td colSpan={colCount}><TableLoadingCell /></td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={colCount} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>暂无数据</td></tr>
          ) : sortedRows.map((r, i) => (
            <tr key={i}>
              <td style={{ whiteSpace: 'nowrap' }}>{r.date}</td>
              {showDevice && <td>{r.pub_client || '-'}</td>}
              {showChannel && <td>{r.channel || '-'}</td>}
              <td>{r.user_type}</td>
              <td style={tdNum}>{fmtInt(r.all_site_uv)}</td>
              <td style={tdNum}>{fmtInt(r.jump_uv)}</td>
              <td style={tdNum}>{fmtInt(r.no_jump_uv)}</td>
              <td style={tdNum}>{fmtPct(r.bounce_rate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {loading && rows.length > 0 && <TableLoadingOverlay />}
    </div>
  );
}

// ── 主页面 ──────────────────────────────────────────────────────────
export default function SiteBounceRate() {
  const { shopName: site } = useSiteShopName();
  const range = defaultRange();
  const [startDate, setStartDate] = useState(range.start);
  const [endDate, setEndDate] = useState(range.end);
  const [datePreset, setDatePreset] = useState<DatePreset>('30d');
  const [userType, setUserType] = useState('全部');
  const [device, setDevice] = useState('全部');
  const [detailGroup, setDetailGroup] = useState<DetailGroup>('user_type');

  const [summary, setSummary] = useState<BounceSummary | null>(null);
  const [trend, setTrend] = useState<BounceTrendRow[]>([]);
  const [channel1, setChannel1] = useState<BounceChannelRow[]>([]);
  const [channel2, setChannel2] = useState<BounceChannelRow[]>([]);
  const [detail, setDetail] = useState<BounceDetailRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');

  const baseParams = useCallback((): BounceParams => ({
    site, start_date: startDate, end_date: endDate,
  }), [site, startDate, endDate]);

  const fetchMain = useCallback(async () => {
    if (!site) return;
    setLoading(true);
    setError('');
    try {
      const [s, t, c1, c2] = await Promise.all([
        siteDataApi.getBounceSummary({ ...baseParams(), user_type: userType, pub_client: device }),
        siteDataApi.getBounceTrend(baseParams()),
        siteDataApi.getBounceChannel({ ...baseParams(), level: 1 }),
        siteDataApi.getBounceChannel({ ...baseParams(), level: 2 }),
      ]);
      setSummary(s.data ?? null);
      setTrend(t.data?.list ?? []);
      setChannel1(c1.data?.list ?? []);
      setChannel2(c2.data?.list ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [site, baseParams, userType, device]);

  const fetchDetail = useCallback(async () => {
    if (!site) return;
    setDetailLoading(true);
    try {
      const d = await siteDataApi.getBounceDetail({ ...baseParams(), group_by: detailGroup });
      setDetail(d.data?.list ?? []);
    } catch {
      setDetail([]);
    } finally {
      setDetailLoading(false);
    }
  }, [site, baseParams, detailGroup]);

  useEffect(() => { if (site) fetchMain(); }, [site, startDate, endDate, userType, device, fetchMain]);
  useEffect(() => { if (site) fetchDetail(); }, [site, startDate, endDate, detailGroup, fetchDetail]);

  useMarketingViewSection('page', {
    label: '页面筛选', state: { startDate, endDate, datePreset, userType, device, detailGroup },
    restore: value => { setStartDate(value.startDate); setEndDate(value.endDate); setDatePreset(value.datePreset); setUserType(value.userType); setDevice(value.device); setDetailGroup(value.detailGroup); },
    loading: loading || detailLoading, query: () => { void fetchMain(); void fetchDetail(); },
    resolve: value => { if (value.datePreset === 'custom') return value; const range = presetRange(value.datePreset); return { ...value, startDate: range.start, endDate: range.end }; },
    summary: startDate + ' ～ ' + endDate + ' · ' + userType + ' · ' + device,
    autoQuery: true,
  });

  const onDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    if (preset === 'custom') return;
    const next = presetRange(preset);
    setStartDate(next.start);
    setEndDate(next.end);
  };

  // 趋势图 series：四类用户类型
  const trendSeries: LineSeries[] = USER_TYPES.map(ut => ({
    name: ut,
    data: trend.filter(r => r.user_type === ut).map(r => [r.date, r.bounce_rate] as [string, number]),
  })).filter(s => s.data.length > 0);
  const commonParams = { site, start_date: startDate, end_date: endDate };

  return (
    <div className="page-section visible">
      {/* 筛选栏 */}
      <div className="data-filter-card">
        <div className="data-filter-row time">
          <span className="data-filter-inline-label">日期范围</span>
          <div className="page-tabs" style={{ marginBottom: 0 }}>
            {DATE_PRESETS.map(preset => (
              <div
                key={preset.key}
                className={`page-tab${datePreset === preset.key ? ' active' : ''}`}
                onClick={() => onDatePreset(preset.key)}
              >
                {preset.label}
              </div>
            ))}
          </div>
          {datePreset === 'custom' && (
          <div className="data-filter-date-range">
            <input type="date" className="form-input" value={startDate} max={endDate || undefined} onChange={e => setStartDate(e.target.value)} />
            <span style={{ color: 'var(--text3)' }}>→</span>
            <input type="date" className="form-input" value={endDate} min={startDate || undefined} onChange={e => setEndDate(e.target.value)} />
          </div>
          )}
        </div>
      </div>

      <div className="data-filter-card">
        <div className="data-filter-row compact">
          <Field label="用户类型(汇总卡)">
              <ReportLabelSelect dataset="bounce_rate" field="user_group_day" pageFields={['user_group_day', 'pub_client']} includeAll value={[userType]} onChange={([next]) => setUserType(next ?? '全部')} single />
          </Field>
          <Field label="终端(汇总卡)">
              <ReportLabelSelect dataset="bounce_rate" field="pub_client" pageFields={['user_group_day', 'pub_client']} includeAll value={[device]} onChange={([next]) => setDevice(next ?? '全部')} single />
          </Field>
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8, background: 'rgba(220,38,38,.08)', color: '#dc2626', fontSize: 13 }}>{error}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <ReportExportButton
          reportKey="site_bounce_summary"
          title="跳出率监控汇总"
          fileName={`跳出率监控汇总_${startDate}_${endDate}.csv`}
          params={{ ...commonParams, user_type: userType, pub_client: device }}
          columns={bounceSummaryExportColumns}
          disabled={!site}
        />
      </div>

      {/* 汇总卡 */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <SummaryCard label="全站用户" value={summary ? fmtInt(summary.all_site_uv) : '-'} sub="全站uv" />
        <SummaryCard label="跳出用户数" value={summary ? fmtInt(summary.jump_uv) : '-'} />
        <SummaryCard label="未跳出用户数" value={summary ? fmtInt(summary.no_jump_uv) : '-'} />
        <SummaryCard label="跳出率" value={summary ? fmtPct(summary.bounce_rate) : '-'} />
      </div>

      {/* 趋势图 */}
      <div className="card" style={{ position: 'relative', padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>核心指标趋势图 - 跳出率趋势</span>
          <ReportExportButton
            reportKey="site_bounce_trend"
            title="跳出率趋势"
            fileName={`跳出率趋势_${startDate}_${endDate}.csv`}
            params={commonParams}
            columns={bounceTrendExportColumns}
            disabled={!site}
          />
        </div>
        {trendSeries.length === 0
          ? (loading ? <TableLoadingState /> : <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>暂无数据</div>)
          : <BounceLineChart series={trendSeries} />}
        {loading && trendSeries.length > 0 && <TableLoadingOverlay />}
      </div>

      {/* 渠道跳出率监控 */}
      <ChannelBounceTable
        level={1}
        rows={channel1}
        loading={loading}
        extra={<ReportExportButton reportKey="site_bounce_channel" title="一级渠道跳出率" fileName={`一级渠道跳出率_${startDate}_${endDate}.csv`} params={{ ...commonParams, level: 1 }} columns={bounceChannelExportColumns} disabled={!site} />}
      />
      <ChannelBounceTable
        level={2}
        rows={channel2}
        loading={loading}
        extra={<ReportExportButton reportKey="site_bounce_channel" title="二级渠道跳出率" fileName={`二级渠道跳出率_${startDate}_${endDate}.csv`} params={{ ...commonParams, level: 2 }} columns={bounceChannelExportColumns} disabled={!site} />}
      />

      {/* 明细数据 */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>明细数据</span>
          <div className="page-tabs" style={{ marginBottom: 0 }}>
            {DETAIL_TABS.map(t => (
              <div key={t.key}
                className={`page-tab${detailGroup === t.key ? ' active' : ''}`}
                onClick={() => setDetailGroup(t.key)}
              >{t.label}</div>
            ))}
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <ReportExportButton
              reportKey="site_bounce_detail"
              title={`明细数据-${DETAIL_TABS.find(t => t.key === detailGroup)?.label ?? ''}`}
              fileName={`跳出率明细_${detailGroup}_${startDate}_${endDate}.csv`}
              params={{ ...commonParams, group_by: detailGroup }}
              columns={bounceDetailExportColumns(detailGroup)}
              disabled={!site}
            />
          </div>
        </div>
        <DetailTable group={detailGroup} rows={detail} loading={detailLoading} />
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="data-filter-field">
      <label className="data-filter-label">{label}</label>
      {children}
    </div>
  );
}
